import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { ArrowLeftCircle, LayoutList } from "lucide-react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  PointElement,
  LineElement
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  RadialLinearScale,
  Tooltip,
  Legend,
  PointElement,
  LineElement
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sectionId = searchParams.get("sectionId");
  const resultId = searchParams.get("resultId");

  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sectionInfo, setSectionInfo] = useState(null);
  const [email, setEmail] = useState("");
  const role = localStorage.getItem("userRole") || "student";

  useEffect(() => {
    if (role !== "admin") {
      toast.error("Results are locked for assessees");
      navigate("/dashboard");
      return;
    }
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      navigate("/login");
      return;
    }
    setEmail(userEmail);
    fetchResults(userEmail);
  }, [navigate, sectionId, resultId]);

  const fetchResults = async (userEmail) => {
    setLoading(true);
    try {
      let response;
      if (resultId) {
        response = await axios.get(`${API}/result/${resultId}`);
      } else if (sectionId) {
        response = await axios.get(`${API}/results/${userEmail}`, { params: { sectionId } });
      } else {
        response = await axios.get(`${API}/results/${userEmail}`);
      }
      setResults(response.data);
      const secId = response.data?.section_id;
      if (secId) {
        try {
          const secRes = await axios.get(`${API}/sections/${secId}/questions`);
          setSectionInfo(secRes.data?.section || null);
        } catch (_) {
          setSectionInfo(null);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load results");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  const answers = results?.scores?.answers || [];
  const percentage = results
    ? results.percentage || Math.round((results.total_score / (results.max_score || 1)) * 100)
    : 0;
  const barData = useMemo(() => {
    if (!answers.length) return null;
    return {
      labels: answers.map((_, idx) => `Q${idx + 1}`),
      datasets: [
        {
          label: "Selected weight",
          data: answers.map((a) => a.selected_option?.weight ?? 0),
          backgroundColor: "rgba(59, 130, 246, 0.7)",
          borderRadius: 6
        },
        {
          label: "Max weight",
          data: answers.map((a) => a.max_weight ?? 0),
          backgroundColor: "rgba(16, 185, 129, 0.5)",
          borderRadius: 6
        }
      ]
    };
  }, [answers]);

  const donutData = useMemo(() => {
    if (!results) return null;
    const earned = results.total_score || 0;
    const possible = results.max_score || 0;
    const remaining = Math.max(possible - earned, 0);
    return {
      labels: ["Earned", "Remaining"],
      datasets: [
        {
          data: [earned, remaining],
          backgroundColor: ["#10b981", "#e5e7eb"],
          borderWidth: 0
        }
      ]
    };
  }, [results]);

  const radarData = useMemo(() => {
    if (!answers.length) return null;
    return {
      labels: answers.map((a, idx) => a.question_text?.slice(0, 20) || `Q${idx + 1}`),
      datasets: [
        {
          label: "Score",
          data: answers.map((a) => a.selected_option?.weight ?? 0),
          backgroundColor: "rgba(99, 102, 241, 0.2)",
          borderColor: "#6366f1",
          pointBackgroundColor: "#6366f1"
        }
      ]
    };
  }, [answers]);

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <p>Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!results) {
    return (
      <div>
        <Navbar />
        <div className="page-container">
          <div className="auth-card">
            <p>No results found</p>
            <button className="btn-primary" onClick={handleBackToDashboard} style={{ marginTop: '1rem' }}>
              Go back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <div className="page-container">
        <div className="results-container" data-testid="results-container">
          <div className="results-card">
            <div className="results-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <button className="btn-secondary" onClick={handleBackToDashboard} style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", marginBottom: "0.75rem" }}>
                  <ArrowLeftCircle size={16} />
                  Dashboard
                </button>
                <h1>{results.section_name || "Section Result"}</h1>
                <p>Completed on {new Date(results.completed_at).toLocaleString()}</p>
              </div>
            </div>

            <div className="score-cards">
              <div className="score-card">
                <h3>Total Score</h3>
                <div className="score">{results.total_score} / {results.max_score || 0}</div>
              </div>
              <div className="score-card">
                <h3>Percentage</h3>
                <div className="score">{percentage}%</div>
              </div>
              <div className="score-card">
                <h3>Questions Answered</h3>
                <div className="score">{answers.length}</div>
              </div>
            </div>

            <div className="chart-grid">
              {barData && (
                <div className="chart-card">
                  <div className="chart-card-header">
                    <LayoutList size={18} />
                    <h3>Per Question Weights</h3>
                  </div>
                  <Bar data={barData} options={{ responsive: true, plugins: { legend: { position: "bottom" } } }} />
                </div>
              )}
              {donutData && (
                <div className="chart-card">
                  <div className="chart-card-header">
                    <LayoutList size={18} />
                    <h3>Score Share</h3>
                  </div>
                  <Doughnut
                    data={donutData}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: "bottom" }
                      }
                    }}
                  />
                </div>
              )}
              {radarData && (
                <div className="chart-card">
                  <div className="chart-card-header">
                    <LayoutList size={18} />
                    <h3>Question Emphasis</h3>
                  </div>
                  <Radar
                    data={radarData}
                    options={{
                      responsive: true,
                      scales: { r: { ticks: { display: false } } },
                      plugins: { legend: { position: "bottom" } }
                    }}
                  />
                </div>
              )}
            </div>

            <div className="panel-card" style={{ marginTop: "1rem" }}>
              <div className="panel-card-header">
                <div>
                  <h3>Section Report</h3>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.75rem" }}>
                <div className="stat-card" style={{ padding: "1rem" }}>
                  <div className="stat-label">Section</div>
                  <div className="stat-value" style={{ fontSize: "1.1rem" }}>
                    {sectionInfo?.name || results.section_name || "N/A"}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: "1rem" }}>
                  <div className="stat-label">Questions</div>
                  <div className="stat-value" style={{ fontSize: "1.1rem" }}>
                    {sectionInfo?.question_count ?? (barData?.labels?.length || "N/A")}
                  </div>
                </div>
                <div className="stat-card" style={{ padding: "1rem" }}>
                  <div className="stat-label">Completed</div>
                  <div className="stat-value" style={{ fontSize: "1.1rem" }}>
                    {new Date(results.completed_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;
